'use client';

import React, { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Heart, ShieldCheck } from 'lucide-react';
import { calculateNadiDosha, NadiDoshaResult, CreateBirthProfileInput } from '@/lib/api/client';
import { QuickCalculatorForm } from '@/components/quick-calculator/QuickCalculatorForm';

export interface NadiDoshaCalculatorClientProps {
  initialResult?: NadiDoshaResult | null;
  initialError?: string | null;
}

export function NadiDoshaCalculatorClient({
  initialResult = null,
  initialError = null
}: NadiDoshaCalculatorClientProps) {
  const t = useTranslations('nadiDosha');
  const tGlossary = useTranslations('glossary');

  const [personA, setPersonA] = useState<CreateBirthProfileInput | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(initialError);
  const [result, setResult] = useState<NadiDoshaResult | null>(initialResult);

  const handleCalculate = async (inputA: CreateBirthProfileInput, inputB: CreateBirthProfileInput) => {
    setLoading(true);
    setError(null);
    try {
      const res = await calculateNadiDosha({ personA: inputA, personB: inputB });
      setResult(res);
    } catch (err: any) {
      setError(err.message || t('computingMsg'));
    } finally {
      setLoading(false);
    }
  };

  const getNadiName = (nadiStr?: string | null): string => {
    if (!nadiStr) return '';
    const key = nadiStr.toLowerCase();
    if (key.includes('adi') || key.includes('aadi')) {
      try {
        const val = tGlossary('nadiTypes.adi');
        if (val && !val.startsWith('nadiTypes.')) return val;
      } catch (_) {}
    }
    if (key.includes('madhya')) {
      try {
        const val = tGlossary('nadiTypes.madhya');
        if (val && !val.startsWith('nadiTypes.')) return val;
      } catch (_) {}
    }
    if (key.includes('antya')) {
      try {
        const val = tGlossary('nadiTypes.antya');
        if (val && !val.startsWith('nadiTypes.')) return val;
      } catch (_) {}
    }
    return nadiStr;
  };

  const getObservationDetails = (res: NadiDoshaResult): string => {
    const nadiA = getNadiName(res.nadi_a || '');
    const nadiB = getNadiName(res.nadi_b || '');
    if (res.reason_code === 'COMPATIBLE_DIFFERENT_NADI' || (res.nadi_a && res.nadi_b && res.nadi_a !== res.nadi_b)) {
      return t('reasons.COMPATIBLE_DIFFERENT_NADI', { nadiA, nadiB });
    }
    if (res.reason_code === 'EXCEPTION_SAME_SIGN_DIFF_NAK') {
      return t('reasons.EXCEPTION_SAME_SIGN_DIFF_NAK', { nadiA });
    }
    if (res.reason_code === 'EXCEPTION_SAME_NAK_DIFF_SIGN') {
      return t('reasons.EXCEPTION_SAME_NAK_DIFF_SIGN', { nadiA });
    }
    if (res.isCompatible && res.exceptionApplies) {
      return t('reasons.EXCEPTION_SAME_SIGN_DIFF_NAK', { nadiA });
    }
    return t('reasons.DOSHA_APPLIES', { nadiA: nadiA || nadiB });
  };

  return (
    <div className="space-y-8" data-testid="nadi-dosha-client">
      {/* Header / Eyebrow Banner */}
      <div className="bg-[var(--card)] border border-[var(--line)] rounded-2xl p-6 sm:p-8 text-center space-y-3 shadow-xs">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[var(--line)] bg-[var(--card-warm)] text-xs font-mono text-[var(--gold)]">
          <Heart className="w-3.5 h-3.5" />
          <span>{t('eyebrow')}</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-serif font-semibold tracking-tight text-[var(--foreground)]">
          {t('title')}
        </h1>
        <p className="text-xs sm:text-sm text-[var(--muted)] max-w-2xl mx-auto leading-relaxed">
          {t('subtitle')}
        </p>
      </div>

      {/* Input Forms */}
      <div className="bg-[var(--card)] border border-[var(--line)] rounded-2xl p-6 sm:p-8 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <QuickCalculatorForm
              title={t('partnerA')}
              subtitle={t('partnerASubtitle')}
              loading={false}
              onCalculate={(data) => setPersonA(data)}
            />
          </div>
          <div className="space-y-4">
            <QuickCalculatorForm
              title={t('partnerB')}
              subtitle={t('partnerBSubtitle')}
              loading={loading}
              onCalculate={(inputB) => {
                if (!personA) {
                  setError(t('partnerAFirst'));
                  return;
                }
                handleCalculate(personA, inputB);
              }}
            />
          </div>
        </div>

        {loading && (
          <div className="text-center py-6 text-xs text-[rgba(220,236,255,0.7)] font-mono animate-pulse">
            {t('computingMsg')}
          </div>
        )}

        {error && (
          <div data-testid="nadi-dosha-error-panel" className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-500 text-xs font-mono text-center">
            {error}
          </div>
        )}
      </div>

      {/* Results Display */}
      {result && (
        <div className="bg-[var(--card)] border border-[var(--line)] rounded-2xl p-6 sm:p-8 space-y-6 shadow-md" data-testid="nadi-dosha-result-panel">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 border-b border-[var(--line)] pb-4">
            <div>
              <span className="text-[10px] font-mono text-[var(--muted)] uppercase tracking-wider">
                {t('scoreHeading')}
              </span>
              <h3 className="text-2xl font-serif font-semibold text-[var(--foreground)]">
                {t('scoreFormat', { score: result.score, maxScore: result.maxScore })}
              </h3>
            </div>
            <div className={`px-4 py-2 rounded-xl text-xs font-bold font-mono border ${
              result.isCompatible 
                ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30' 
                : 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/30'
            }`}>
              {result.isCompatible ? t('badgeCompatible') : t('badgeDosha')}
            </div>
          </div>

          <div className="p-4 rounded-xl bg-[var(--background)] border border-[var(--line)] space-y-2">
            <div className="text-xs font-bold text-[var(--gold)] uppercase font-mono">
              {t('detailsHeading')}
            </div>
            <p className="text-xs text-[var(--foreground)] leading-relaxed">
              {getObservationDetails(result)}
            </p>
          </div>

          {result.exceptionApplies && (
            <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-600 dark:text-amber-400 text-xs space-y-1">
              <div className="font-bold flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4" />
                <span>{t('cancelledTitle')}</span>
              </div>
              <p className="text-[11px] leading-relaxed">
                {t('cancelledDesc')}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
